import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import client from "@/lib/api";
import { Bot, Send, X, Loader2 } from "lucide-react";

// Floating AI Assistant — available on every console so operators get help
// without leaving their call. Shares the same GPT-5.4 backend; session "floating".
export const AssistantWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (open) client.get("/assistant/history/floating").then((r) => setMsgs(r.data.messages || [])).catch(() => {});
  }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  if (location.pathname.startsWith("/assistant")) return null; // full page already open

  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: t }, { role: "assistant", text: "" }]);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ text: t, session: "floating" }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const ln of lines) {
          if (!ln.startsWith("data: ")) continue;
          const d = JSON.parse(ln.slice(6));
          if (d.delta) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + d.delta }; return c; });
          if (d.error) setMsgs((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", text: `Error: ${d.error}` }; return c; });
        }
      }
    } catch {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: "Connection error — try again." }]);
    }
    setBusy(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] flex flex-col items-end" data-testid="assistant-widget">
      {open && (
        <div className="mb-2 w-[340px] h-[440px] flex flex-col bg-[#0d0e11] border border-[#2C2D35] rounded-md shadow-2xl shadow-black/60 overflow-hidden" data-testid="assistant-widget-panel">
          <div className="shrink-0 px-3 py-2 bg-[#141518] border-b border-[#2C2D35] flex items-center gap-2">
            <Bot size={15} className="text-[#22D3EE]" />
            <div className="min-w-0">
              <div className="font-head font-bold text-xs text-white leading-none">AI ASSISTANT</div>
              <div className="font-mono text-[8px] text-[#6B7280] mt-0.5">GPT-5.4 · advisory only — never dispatch/legal/medical decisions</div>
            </div>
            <button data-testid="assistant-widget-close" onClick={() => setOpen(false)} className="ml-auto text-[#6B7280] hover:text-white"><X size={15} /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-2.5 space-y-1.5">
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[88%] rounded-sm px-2.5 py-1.5 text-[11px] whitespace-pre-wrap ${m.role === "user"
                ? "ml-auto bg-[#22D3EE] text-black" : "bg-[#141518] border border-[#2C2D35] text-[#E5E7EB]"}`}>
                {m.text || (busy && i === msgs.length - 1 ? <Loader2 size={12} className="animate-spin" /> : "")}
              </div>))}
            {!msgs.length && <div className="font-mono text-[10px] text-[#6B7280] text-center mt-8 px-4">Ask about procedures, call types, report wording, radio phrasing or how to use the system.</div>}
            <div ref={endRef} />
          </div>
          <div className="shrink-0 flex gap-1.5 p-2 border-t border-[#2C2D35]">
            <input data-testid="assistant-widget-input" value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Ask the assistant…"
              className="flex-1 bg-[#141518] border border-[#2C2D35] px-2.5 py-1.5 text-[11px] font-mono text-white rounded-sm outline-none focus:border-[#22D3EE]" />
            <button data-testid="assistant-widget-send" onClick={send} disabled={busy}
              className="px-3 py-1.5 bg-[#22D3EE] text-black rounded-sm disabled:opacity-50">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            </button>
          </div>
        </div>
      )}
      <button data-testid="assistant-widget-toggle" onClick={() => setOpen((o) => !o)}
        title="AI Assistant (GPT-5.4)"
        className="w-12 h-12 rounded-full bg-[#22D3EE] text-black flex items-center justify-center shadow-lg shadow-[#22D3EE]/30 hover:scale-105 transition-transform">
        {open ? <X size={20} /> : <Bot size={22} />}
      </button>
    </div>
  );
};
